import { useMemo } from 'react'
import { DEFAULT_QUOTHA_MODE, type QuothaMode } from '../api'
import {
  getKnowledgeScopeLabel,
  getLanguageModeLabel,
  getToneLevelLabel,
  KNOWLEDGE_SCOPE_OPTIONS,
  LANGUAGE_MODE_OPTIONS,
  TONE_LEVEL_OPTIONS,
} from '../constants/chatPreferences'

export type QuickControlsValues = {
  language_mode: string
  knowledge_scope: string
  tone_level: string
   quotha_mode: QuothaMode
  quotha_annotations?: boolean
}

export type QuickControlsCardProps = {
  values: QuickControlsValues
  defaults: QuickControlsValues
  onChange: <K extends keyof QuickControlsValues>(key: K, value: QuickControlsValues[K]) => void
  onSave?: () => void
  saving?: boolean
  saved?: boolean
  error?: string | null
  quothaSupported?: boolean
  variant?: 'compact' | 'full'
  saveLabel?: string
}

const QuickControlsCard = ({
  values,
  defaults,
  onChange,
  onSave,
  saving,
  saved,
  error,
  quothaSupported = true,
  variant = 'full',
  saveLabel = 'Save',
}: QuickControlsCardProps) => {
  const getQuothaModeLabel = (value?: QuothaMode | string | null): string => {
    if (value === 'on') return 'Quotha On'
    if (value === 'off') return 'Quotha Off'
    return 'Quotha Auto'
  }

  const summaryPills = useMemo(() => {
    const pills = [
      getLanguageModeLabel(values.language_mode || defaults.language_mode),
      getKnowledgeScopeLabel(values.knowledge_scope || defaults.knowledge_scope),
      getToneLevelLabel(values.tone_level || defaults.tone_level),
    ]

    if (quothaSupported) {
      pills.push(getQuothaModeLabel(values.quotha_mode || defaults.quotha_mode || DEFAULT_QUOTHA_MODE))
    }

    return pills
  }, [values, defaults, quothaSupported])

  return (
    <div className={`controls-card controls-card-${variant}`}>
      <div className="control-summary" aria-label="Current control summary">
        {summaryPills.map((label) => (
          <span key={label} className="control-pill">
            {label}
          </span>
        ))}
        {saved && <span className="control-saved">Saved</span>}
        {saving && <span className="control-saving">Saving…</span>}
      </div>

      {error && <p className="error-state">{error}</p>}

      <div className="quick-controls">
        <label htmlFor={`language-mode-${variant}`}>Language style</label>
        <select
          id={`language-mode-${variant}`}
          className="control-select"
          value={values.language_mode ?? defaults.language_mode}
          onChange={(event) => onChange('language_mode', event.target.value)}
        >
          {LANGUAGE_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label htmlFor={`knowledge-scope-${variant}`}>Knowledge scope</label>
        <select
          id={`knowledge-scope-${variant}`}
          className="control-select"
          value={values.knowledge_scope ?? defaults.knowledge_scope}
          onChange={(event) => onChange('knowledge_scope', event.target.value)}
        >
          {KNOWLEDGE_SCOPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label htmlFor={`tone-level-${variant}`}>Tone</label>
        <select
          id={`tone-level-${variant}`}
          className="control-select"
          value={values.tone_level ?? defaults.tone_level}
          onChange={(event) => onChange('tone_level', event.target.value)}
        >
          {TONE_LEVEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {quothaSupported && (
          <>
            <label htmlFor={`quotha-mode-${variant}`}>Quotha mode</label>
            <select
              id={`quotha-mode-${variant}`}
              className="control-select"
              value={values.quotha_mode ?? defaults.quotha_mode ?? DEFAULT_QUOTHA_MODE}
              onChange={(event) => onChange('quotha_mode', event.target.value as QuothaMode)}
            >
              <option value="off">Off</option>
              <option value="auto">Auto</option>
              <option value="on">On</option>
            </select>
          </>
        )}
      </div>

      {onSave && (
        <div className="control-actions">
          <button type="button" className="button primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      )}
    </div>
  )
}

export default QuickControlsCard
